import { useState } from "react";
import { Check, Plus } from "lucide-react";
import { RegistrationForm } from "./RegistrationForm";
import { FAQ, HOST, COMPLIANCE, COPYRIGHT, BG_IMAGES } from "../lib/content";
import { Reveal, scrollToId } from "../lib/anim";

export const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative overflow-hidden isolate px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="faq-section">
      <div className="absolute inset-y-0 right-0 w-full md:w-[55%] -z-10 pointer-events-none" data-testid="faq-bg-image">
        <img src={BG_IMAGES.sunriseHome} alt="" data-parallax="0.07" className="w-full h-full object-cover opacity-[0.18] scale-110" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0E2A54 0%, rgba(9,30,66,0.6) 50%, rgba(9,30,66,0.3) 100%), linear-gradient(180deg, #0E2A54 0%, transparent 30%, transparent 70%, #0E2A54 100%)" }} />
      </div>
      <Reveal>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-6">[008] — FAQ</p>
        <h2 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(30px,4vw,54px)] text-[#D9E4F5] mb-14">
          COMMON QUESTIONS
        </h2>
      </Reveal>
      <div className="max-w-3xl border-t border-white/10">
        {FAQ.map((item, i) => (
          <Reveal key={i}>
            <div className="border-b border-white/10">
              <button
                data-testid={`faq-accordion-${i + 1}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center gap-5 py-6 text-left group"
              >
                <span className="font-mono text-xs text-[#FFFFFF]">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 text-[15px] font-semibold text-[#D9E4F5] group-hover:text-[#FFFFFF] transition-colors duration-200">
                  {item.q}
                </span>
                <Plus className={`w-4 h-4 text-[#FFFFFF] shrink-0 transition-transform duration-300 ${open === i ? "rotate-45" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-[max-height,opacity] duration-400 ${open === i ? "max-h-64 opacity-100" : "max-h-0 opacity-0"}`}>
                <p className="pb-7 pl-10 text-[14px] text-white/50 leading-[1.75]">{item.a}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export const FinalRegister = ({ attribution }) => (
  <section
    id="register"
    className="relative px-6 md:px-12 lg:px-16 py-28 md:py-40 bg-white/[0.015] grid-bg overflow-hidden"
    data-testid="final-register-section"
  >
    <div className="absolute inset-0 pointer-events-none" data-testid="register-bg-image">
      <img src={BG_IMAGES.houseKey} alt="" data-parallax="0.08" className="w-full h-full object-cover opacity-25 scale-110" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(9,30,66,0.5) 0%, rgba(9,30,66,0.88) 55%, #0E2A54 100%), linear-gradient(180deg, #0E2A54 0%, transparent 30%, transparent 70%, #0E2A54 100%)",
        }}
      />
    </div>
    <div data-parallax="0.55" className="absolute left-0 top-[12%] w-[560px] h-[560px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.14) 0%, transparent 70%)" }} />
    <div className="relative grid md:grid-cols-2 gap-14 lg:gap-24 items-center">
      <Reveal>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-6">[009] — BOOK YOUR CALL</p>
        <h2 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(36px,5vw,64px)] text-[#D9E4F5] mb-10">
          YOUR NEXT MOVE STARTS WITH A CLEARER <span className="text-[#FFFFFF]">MAP.</span>
        </h2>
        <ul className="space-y-4">
          {[
            "100% free — $0 cost to book or attend",
            "No insurance application, no credit pull",
            "Personalized qualification review — a straight answer either way",
            "Zero obligation to buy anything, ever · Results vary",
          ].map((t, i) => (
            <li key={i} className="flex items-center gap-3 text-[15px] text-white/70" data-testid={`final-trust-bullet-${i + 1}`}>
              <Check className="w-4 h-4 text-[#FFFFFF] shrink-0" /> {t}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="border border-white/12 bg-[#091E42]/45 backdrop-blur-xl p-6 md:p-8" data-testid="final-registration-card">
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#FFFFFF] mb-5">FREE 1-ON-1 STRATEGY CALL · $0 · NO OBLIGATION</p>
          <RegistrationForm variant="final" attribution={attribution} />
        </div>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="relative border-t border-white/10 px-6 md:px-12 lg:px-16 pt-24 pb-32 md:pb-16 overflow-hidden" data-testid="footer-section">
    <p data-parallax="0.2" className="absolute bottom-0 left-1/2 -translate-x-1/2 font-display uppercase whitespace-nowrap leading-none select-none pointer-events-none text-[14vw] text-white/[0.03]">
      DEBT-FREE LIFE
    </p>
    <div className="relative grid md:grid-cols-3 gap-12 mb-16">
      <div>
        <p className="font-mono text-[10px] tracking-[0.3em] text-[#FFFFFF] mb-5">HOST</p>
        <p className="text-sm text-white/70 mb-1.5">{HOST.name}</p>
        <p className="text-sm text-white/45 mb-1.5">{HOST.company}</p>
        <p className="font-mono text-[11px] text-white/35 mb-1.5">License # {HOST.license}</p>
        <p className="font-mono text-[11px] text-white/35">Licensed in {HOST.licenseStates}</p>
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.3em] text-[#FFFFFF] mb-5">CONTACT</p>
        <p className="text-sm text-white/45 mb-1.5">650 S. Shackleford Rd. Ste. 400</p>
        <p className="text-sm text-white/45 mb-1.5">Little Rock, AR 72211</p>
        <p className="text-sm text-white/45 mb-1.5">Phone: (718) 413-3927</p>
        <p className="text-sm text-white/45">Email: najiyyahq@gmail.com</p>
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.3em] text-[#FFFFFF] mb-5">LEGAL</p>
        {[
          ["Privacy Policy", "/privacy-policy", "privacy"],
          ["Terms of Service", "/terms", "terms"],
        ].map(([label, href, key]) => (
          <a key={key} href={href} data-testid={`footer-legal-${key}`} className="block text-sm text-white/45 hover:text-[#FFFFFF] transition-colors duration-200 mb-1.5">
            {label}
          </a>
        ))}
      </div>
    </div>
    <div className="relative border-t border-white/10 pt-8">
      <p id="disclaimer" className="text-[11px] text-white/30 leading-[1.8] max-w-4xl mb-6" data-testid="footer-compliance">{COMPLIANCE}</p>
      <p className="font-mono text-[10px] text-white/20" data-testid="footer-copyright">{COPYRIGHT}</p>
    </div>
  </footer>
);

export const MobileStickyCta = () => (
  <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden p-3 bg-[#091E42]/70 backdrop-blur-xl border-t border-white/10" data-testid="mobile-sticky-cta">
    <button
      data-cta="true"
      data-testid="mobile-sticky-cta-btn"
      onClick={() => scrollToId("#register")}
      className="w-full flex items-center justify-center gap-2 bg-[#FFFFFF] text-[#0E2A54] font-bold text-sm tracking-wide py-4"
    >
      <Check className="w-4 h-4" /> BOOK MY FREE CALL
    </button>
  </div>
);
